import { useEffect, type RefObject } from "react";
import {
  scheduleCalendarLayoutRefresh,
  shouldSkipCalendarLayoutRefresh,
} from "./calendar-layout-refresh";

/** Re-measure react-big-calendar after tab focus or container resize (e.g. sidebar). */
export function useResourceCalendarLayoutRefresh(containerRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    let cancelScheduled: (() => void) | undefined;
    const triggerResize = () => {
      cancelScheduled?.();
      cancelScheduled = scheduleCalendarLayoutRefresh();
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        triggerResize();
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    const resizeObserver = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (
        shouldSkipCalendarLayoutRefresh({
          visibilityState: document.visibilityState,
          width: box?.width,
          height: box?.height,
        })
      ) {
        return;
      }
      triggerResize();
    });
    resizeObserver.observe(root);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      resizeObserver.disconnect();
      cancelScheduled?.();
    };
  }, [containerRef]);
}
